export type FeatureId =
  | 'counterbalance'
  | 'probeTrials'
  | 'assentBreak'
  | 'maintenanceProbes'
  | 'progressGraph'
  | 'masteryCriteria'
  | 'rewards'
  | 'rewardTotal'
  | 'rewardSpending'
  | 'thinning'
  | 'reportExport'
  | 'preferenceAssessment'
  | 'promptCapture'
  | 'multipleExemplars'
  | 'programPackages'
  | 'games'
  | 'secondAdult';

export type FeatureGroup = 'teaching' | 'progress' | 'rewards' | 'sharing';

export type FeatureDef = {
  id: FeatureId;
  group: FeatureGroup;
  label: string;
  hint: string;
  ready: boolean;
};

export const FEATURE_GROUPS: Record<FeatureGroup, string> = {
  teaching: 'How teaching runs',
  progress: 'Measuring progress',
  rewards: 'Rewards',
  sharing: 'Sharing and access',
};

export const FEATURES: readonly FeatureDef[] = [
  {
    id: 'counterbalance',
    group: 'teaching',
    ready: true,
    label: 'Move the answer around evenly',
    hint: 'Spreads the right answer across every position instead of leaving it to chance, so a learner cannot get things right by keeping to one spot.',
  },
  {
    id: 'probeTrials',
    group: 'teaching',
    ready: true,
    label: 'Start the day with a clean check',
    hint: 'The first go at a program each day gives no help and no reward, so you can see what the learner can do on their own.',
  },
  {
    id: 'assentBreak',
    group: 'teaching',
    ready: true,
    label: 'Let the learner ask for a break',
    hint: 'Puts a break button on the board. Asking to stop is recorded as an answer in itself, never as a mistake.',
  },
  {
    id: 'multipleExemplars',
    group: 'teaching',
    ready: true,
    label: 'Teach a thing by more than one name',
    hint: 'A cup can also be a mug. Give a thing its other words and the board shows a different one from time to time, so the learner comes to know the thing rather than one exact word. Never two of them at once — that would make two answers right.',
  },
  {
    id: 'promptCapture',
    group: 'teaching',
    ready: true,
    label: 'Record how much help was given',
    hint: 'Keeps the help the learner had and how long they took on every go, instead of you having to remember it.',
  },
  {
    id: 'programPackages',
    group: 'teaching',
    ready: true,
    label: 'Add programs in a set',
    hint: 'Group the programs you use together into a named set — a starting set for a new learner, say — then add them all at once instead of one at a time. Put a program into a set from its own row, naming a new set there if the one you want does not exist yet. Adding a set copies the programs as they are now, so editing the set later never rewrites what a learner is already working on.',
  },
  {
    id: 'games',
    group: 'rewards',
    ready: true,
    label: 'Games tab',
    hint: 'Adds a Games tab under Programs, with games for free time or as a reward: Reversi for two players and Word Tiles. Each game shows its options first, then starts. They can be played with a subscription or during the full trial.',
  },
  {
    id: 'masteryCriteria',
    group: 'progress',
    ready: true,
    label: 'Decide when something is learned',
    hint: 'Set the rule — how well, across how many sittings — and the app moves it on by itself when the learner gets there.',
  },
  {
    id: 'maintenanceProbes',
    group: 'progress',
    ready: true,
    label: 'Bring learned things back now and then',
    hint: 'Quietly returns to things already learned, with longer gaps each time, so they are not forgotten.',
  },
  {
    id: 'progressGraph',
    group: 'progress',
    ready: true,
    label: 'Show a progress chart',
    hint: 'A chart for each thing being taught, marked wherever you changed how it is run, so the change and its effect sit together.',
  },
  {
    id: 'reportExport',
    group: 'progress',
    ready: true,
    label: 'Save a report to share',
    hint: 'Turns any period into a file you can send to a parent, a school or a supervisor.',
  },
  {
    id: 'rewards',
    group: 'rewards',
    ready: true,
    label: 'Collect rewards for work',
    hint: 'Something a learner collects — stars, points, whatever you call them. Switching this on adds a Rewards tab where you choose what they are and when they arrive.',
  },
  {
    id: 'rewardTotal',
    group: 'rewards',
    ready: true,
    label: 'Show how many they have collected',
    hint: 'The running total for the learner, on the home page and quietly on the board while they play. It only appears when rewards are switched on.',
  },
  {
    id: 'rewardSpending',
    group: 'rewards',
    ready: true,
    label: 'Let them spend what they collect',
    hint: 'Make a list of things a learner can choose — extra time on a swing, a favourite song — and say what each costs. They pick from it by tapping their total. Something can cost nothing at all if you would rather it did not. Switched off, stars are simply collected and the total still shows.',
  },
  {
    id: 'thinning',
    group: 'rewards',
    ready: true,
    label: 'Give rewards less often over time',
    hint: 'Starts with frequent rewards and gradually needs more work for each, so they do not become something the learner depends on.',
  },
  {
    id: 'preferenceAssessment',
    group: 'rewards',
    ready: true,
    label: 'Let the learner put the rewards in order',
    hint: 'A short picking game: the learner chooses between the reward pictures until they are in their own order, best first. Whichever they put first becomes the one they collect.',
  },
  {
    id: 'secondAdult',
    group: 'sharing',
    ready: true,
    label: 'Add another adult',
    hint: 'An assistant, teacher or the other parent can run programs and see how a learner is getting on. They cannot change the subscription or delete the account. Invite them from the People tab.',
  },
];

export const FEATURE_DEFAULTS: Record<FeatureId, boolean> = {
  counterbalance: true,
  probeTrials: false,
  assentBreak: false,
  maintenanceProbes: false,
  progressGraph: false,
  masteryCriteria: false,
  rewards: false,
  rewardTotal: false,
  rewardSpending: false,
  thinning: false,
  reportExport: false,
  preferenceAssessment: false,
  promptCapture: false,
  multipleExemplars: false,
  programPackages: false,
  games: false,
  secondAdult: false,
};
